/*
    This file is part of @erc725/erc725.js.
    @erc725/erc725.js is free software: you can redistribute it and/or modify
    it under the terms of the GNU Lesser General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.
    @erc725/erc725.js is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU Lesser General Public License for more details.
    You should have received a copy of the GNU Lesser General Public License
    along with web3.js.  If not, see <http://www.gnu.org/licenses/>.
*/
/**
 * @file lib/utils.ts
 * @author Robert McLeod <@robertdavid010>, Fabian Vogelsteller <fabian@lukso.network>
 * @date 2020
 */
import { checkAddressChecksum, isAddress, keccak256, numberToHex, padLeft, } from 'web3-utils';
import { HASH_FUNCTIONS, SUPPORTED_HASH_FUNCTIONS_LIST, } from './constants';
import { decodeValueContent, decodeValueType, encodeValueContent, encodeValueType, valueContentEncodingMap as valueContentMap, } from './encoder';
/**
 *
 * @param {string} valueContent as per ERC725Schema definition
 * @param {string} valueType as per ERC725Schema definition
 * @param value can contain single value, an array, or an object as required by schema (JSONURL, or ASSETURL)
 * @param {string} [name]
 * @return the encoded value as per the schema
 */
export function encodeKeyValue(valueContent, valueType, value, name) {
    const isSupportedValueContent = valueContentMap[valueContent] || valueContent.substr(0, 2) === '0x';
    if (!isSupportedValueContent) {
        throw new Error(`The valueContent '${valueContent}' 
            for ${name} is not supported.`);
    }
    let result;
    const sameEncoding = valueContentMap[valueContent] &&
        valueContentMap[valueContent].type === valueType.split('[]')[0];
    const isArray = valueType.substr(valueType.length - 2) === '[]';
    // We only loop if the valueType done by abi.encodeParameter can not handle it directly
    if (Array.isArray(value) && !sameEncoding) {
        // value type encoding will handle it?
        // we handle an array element encoding
        const results = [];
        for (let index = 0; index < value.length; index++) {
            const element = value[index];
            results.push(encodeValueContent(valueContent, element));
        }
        result = results;
    }
    else if (!isArray && !Array.isArray(value)) {
        // Straight forward encode
        result = encodeValueContent(valueContent, value);
    }
    else if (sameEncoding) {
        result = value; // leaving this for below
    }
    if (
    // and we only skip bytes regardless
    valueType !== 'bytes' &&
        // Requires encoding because !sameEncoding means both encodings are required
        !sameEncoding) {
        result = encodeValueType(valueType, result);
    }
    else if (isArray && sameEncoding) {
        result = encodeValueType(valueType, result);
    }
    return result;
}
/**
 *
 * @param key The schema key of a schema with keyType = 'Array'
 * @param index An integer representing the intended array index
 * @return The raw bytes key for the array element
 */
export function encodeArrayKey(key, index) {
    return key.substr(0, 34) + padLeft(numberToHex(index), 32).replace('0x', '');
}
/**
 *
 * @param keyName the schema key name
 * @returns a guess of the schema key type
 */
export function guessKeyTypeFromKeyName(keyName) {
    // This function could not work with subsequents keys of an Array
    // It will always assume the given key, if array, is the initial array key.
    const splittedKeyName = keyName.split(':');
    if (splittedKeyName.length === 3) {
        return 'Bytes20MappingWithGrouping';
    }
    if (splittedKeyName.length === 2) {
        if (splittedKeyName[1].substr(0, 2) === '0x') {
            return 'Bytes20Mapping';
        }
        return 'Mapping';
    }
    if (keyName.substring(keyName.length - 2, keyName.length) === '[]') {
        return 'Array';
    }
    return 'Singleton';
}
/**
 *
 * @param name the schema element name
 * @return the name of the key encoded as per specifications
 * @return a string of the encoded schema name
 */
export function encodeKeyName(name) {
    const keyType = guessKeyTypeFromKeyName(name);
    switch (keyType) {
        case 'Bytes20MappingWithGrouping': {
            // bytes4(keccak256(FirstWord)) + bytes4(0) + bytes2(keccak256(SecondWord)) + bytes2(0) + bytes20(address)
            const keyNameSplit = name.split(':');
            return (keccak256(keyNameSplit[0]).substr(0, 10) +
                '00000000' +
                keccak256(keyNameSplit[1]).substr(2, 4) +
                '0000' +
                keyNameSplit[2].substr(0, 40));
        }
        case 'Bytes20Mapping': {
            // bytes8(keccak256(FirstWord)) + bytes4(0) + bytes20(address)
            const keyNameSplit = name.split(':');
            return (keccak256(keyNameSplit[0]).substr(0, 18) +
                '00000000' +
                keyNameSplit[1].substr(2, 40));
        }
        case 'Mapping': {
            // bytes16(keccak256(FirstWord)) + bytes12(0) + bytes4(keccak256(LastWord))
            const keyNameSplit = name.split(':');
            return (keccak256(keyNameSplit[0]).substr(0, 34) +
                '000000000000000000000000' +
                keccak256(keyNameSplit[1]).substr(2, 8));
        }
        case 'Array': // Warning: this can not correctly encode subsequent keys of array, only the initial Array key will work
        case 'Singleton':
            return keccak256(name);
        default:
            return keccak256(name);
    }
}
/**
 *
 * @param schemas An array of ERC725JSONSchema objects
 * @param {string} key A string of either the schema element name, or key
 * @return The requested schema element from the full array of schemas
 */
export function getSchemaElement(schemas, key) {
    const keyHash = key.substr(0, 2) === '0x' ? key : encodeKeyName(key);
    const schemaElement = schemas.find((e) => e.key === keyHash);
    if (!schemaElement) {
        throw new Error('No matching schema found for key: "' + key + '" (' + keyHash + ').');
    }
    return schemaElement;
}
/**
 *
 * @param schema is an object of a schema definitions
 * @param value will be either key-value pairs for a key type of Array, or a single value for type Singleton
 * @return the encoded value for the key as per the supplied schema
 */
export function encodeKey(schema, value) {
    // NOTE: This will not guarantee order of array as on chain. Assumes developer must set correct order
    const lowerCaseKeyType = schema.keyType.toLowerCase();
    switch (lowerCaseKeyType) {
        case 'array': {
            if (!Array.isArray(value)) {
                console.error("Can't encode a non array for key of type array");
                return null;
            }
            const results = [];
            for (let index = 0; index < value.length; index++) {
                const dataElement = value[index];
                if (index === 0) {
                    // This is arrayLength as the first element in the raw array
                    results.push({
                        key: schema.key,
                        value: encodeKeyValue('Number', 'uint256', value.length.toString(), schema.name),
                    });
                }
                results.push({
                    key: encodeArrayKey(schema.key, index),
                    value: encodeKeyValue(schema.valueContent, schema.valueType, dataElement, schema.name),
                });
            }
            return results;
        }
        case 'bytes20mapping':
        case 'bytes20mappingwithgrouping':
        case 'singleton':
        case 'mapping':
            return encodeKeyValue(schema.valueContent, schema.valueType, value, schema.name);
        default:
            console.error('Incorrect data match or keyType in schema from encodeKey(): "' +
                schema.keyType +
                '"');
            return null;
    }
}
/**
 *
 * @param {string} valueContent as per ERC725Schema definition
 * @param {string} valueType as per ERC725Schema definition
 * @param {string} value the encoded value as string
 * @param {string} [name]
 * @return the decoded value as per the schema
 */
export function decodeKeyValue(valueContent, valueType, value, name) {
    // Check for the missing map.
    if (!valueContentMap[valueContent] && valueContent.substr(0, 2) !== '0x') {
        throw new Error('The valueContent "' +
            valueContent +
            '" for "' +
            name +
            '" is not supported.');
    }
    let sameEncoding = valueContentMap[valueContent] &&
        valueContentMap[valueContent].type === valueType.split('[]')[0];
    const isArray = valueType.substr(valueType.length - 2) === '[]';
    // VALUE TYPE
    if (valueType !== 'bytes' && // we ignore because all is decoded by bytes to start with (abi)
        valueType !== 'string' &&
        !isAddress(value) // checks for addresses, since technically an address is bytes?
    ) {
        // eslint-disable-next-line no-param-reassign
        value = decodeValueType(valueType, value);
    }
    // As per exception above, if address and sameEncoding, then the address still needs to be handled
    if (sameEncoding && isAddress(value) && !checkAddressChecksum(value)) {
        sameEncoding = !sameEncoding;
    }
    if (sameEncoding && valueType !== 'string') {
        return value;
    }
    // VALUE CONTENT
    // We are finished if duplicated encoding methods
    if (isArray && Array.isArray(value)) {
        // value must be an array also
        const results = [];
        for (let index = 0; index < value.length; index++) {
            const element = value[index];
            results.push(decodeValueContent(valueContent, element));
        }
        return results;
    }
    return decodeValueContent(valueContent, value);
}
/**
 *
 * @param schema is an object of a schema definitions
 * @param value will be either key-value pairs for a key type of Array, or a single value for type Singleton
 * @return the decoded value/values as per the schema definition
 */
export function decodeKey(schema, value) {
    const lowerCaseKeyType = schema.keyType.toLowerCase();
    switch (lowerCaseKeyType) {
        case 'array': {
            const results = [];
            // If user has requested a key which does not exist in the contract, value will be: 0x and value.find() will fail.
            if (!value || typeof value === 'string') {
                return results;
            }
            const valueElement = value.find((e) => e.key === schema.key);
            // Handle empty/non-existent array
            if (!valueElement) {
                return results;
            }
            const arrayLength = decodeKeyValue('Number', 'uint256', valueElement.value, schema.name) ||
                0;
            // This will not run if no match or arrayLength
            for (let index = 0; index < arrayLength; index++) {
                const dataElement = value.find((e) => e.key === encodeArrayKey(schema.key, index));
                if (dataElement) {
                    results.push(decodeKeyValue(schema.valueContent, schema.valueType, dataElement.value, schema.name));
                }
            } // end for loop
            return results;
        }
        case 'bytes20mapping':
        case 'bytes20mappingwithgrouping':
        case 'singleton':
        case 'mapping': {
            if (Array.isArray(value)) {
                const newValue = value.find((e) => e.key === schema.key);
                // Handle empty or non-values
                if (!newValue) {
                    return null;
                }
                return decodeKeyValue(schema.valueContent, schema.valueType, newValue.value, schema.name);
            }
            return decodeKeyValue(schema.valueContent, schema.valueType, value, schema.name);
        }
        default: {
            console.error('Incorrect data match or keyType in schema from decodeKey(): "' +
                schema.keyType +
                '"');
            return null;
        }
    }
}
/**
 *
 * @param schema schema is an array of objects of schema definitions
 * @param data data is an array of objects of key-value pairs
 * @return: all decoded data as per required by the schema and provided data
 */
export function decodeData(data, schema) {
    return Object.entries(data).reduce((decodedData, [key, value]) => {
        const schemaElement = getSchemaElement(schema, key);
        return Object.assign(Object.assign({}, decodedData), { [schemaElement.name]: decodeKey(schemaElement, value) });
    }, {});
}
/**
 * @param schema an array of schema definitions as per ${@link ERC725JSONSchema}
 * @param data an object of key-value pairs
 */
export function encodeData(data, schema) {
    return Object.entries(data).reduce((accumulator, [key, value]) => {
        const schemaElement = getSchemaElement(schema, key);
        accumulator[key] = {
            value: encodeKey(schemaElement, value),
            key: schemaElement.key,
        };
        return accumulator;
    }, {});
}
export function getHashFunction(hashFunctionNameOrHash) {
    const hashFunction = HASH_FUNCTIONS[hashFunctionNameOrHash];
    if (!hashFunction) {
        throw new Error(`Chosen hashFunction '${hashFunctionNameOrHash}' is not supported.
      Supported hashFunctions: ${SUPPORTED_HASH_FUNCTIONS_LIST}
      `);
    }
    return hashFunction;
}
export function hashData(data, hashFunctionNameOrHash) {
    const hashFunction = getHashFunction(hashFunctionNameOrHash);
    return hashFunction.method(data);
}
/**
 * Hashes the data received with the specified hashing function,
 * and compares the result with the provided hash.
 */
export function isDataAuthentic(data, expectedHash, lowerCaseHashFunction) {
    const dataHash = hashData(data, lowerCaseHashFunction);
    if (dataHash !== expectedHash) {
        console.error(`Hash mismatch, returned JSON hash ("${dataHash}") is different from expected hash "${expectedHash}"`);
        return false;
    }
    return true;
}
/**
 * Transform the object containing the encoded data into an array ordered by keys,
 * for easier handling when writing the data to the blockchain.
 *
 * @param {{
 *   [key: string]: any;
 * }} encodedData This is essentially the object you receive when calling `encodeData(...)`
 * @return {*}  KeyValuePair[] An array of key-value objects
 */
export function flattenEncodedData(encodedData) {
    return (Object.entries(encodedData)
        .reduce((keyValuePairs, [, encodedDataElement]) => {
        if (Array.isArray(encodedDataElement.value)) {
            return keyValuePairs.concat(encodedDataElement.value);
        }
        keyValuePairs.push({
            key: encodedDataElement.key,
            value: encodedDataElement.value,
        });
        return keyValuePairs;
    }, [])
        // sort array of objects by keys, to not be dependent on the order of the object's keys
        .sort((a, b) => {
        if (a.key < b.key)
            return -1;
        return a.key > b.key ? 1 : 0;
    }));
}
//# sourceMappingURL=data:application/json;base64,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